import React, { useState, useEffect } from 'react';
import api, { measurementAPI, drawing2DAPI, drawing3DAPI } from '../../services/api';
import { formatFileSize, formatDate } from '../../utils/formatters';
import { FILE_TYPE, FILE_TYPE_LABELS, getAmendmentCycleShortLabel } from '../../utils/constants';
import { hasDirectorAccess } from '../../utils/rolePermissions';
import UploadFilesModal from '../projects/UploadFilesModal';

// Component to load images with authentication
// Handles both local mode (direct blob) and R2 mode (presigned URL)
const AuthenticatedImage = ({ fileId, fileName, className, alt }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    let objectUrl = null;

    const loadImage = async () => {
      try {
        // Always fetch as blob - works for both modes
        const response = await api.get(`/project-files/${fileId}/download`, {
          responseType: 'blob'
        });

        if (!mounted) return;

        // Check if response is JSON (R2 mode returns JSON with downloadUrl)
        const contentType = response.headers['content-type'] || '';

        if (contentType.includes('application/json')) {
          // R2 mode: parse JSON to get downloadUrl
          const text = await response.data.text();
          const jsonData = JSON.parse(text);
          if (jsonData.downloadUrl) {
            setImageSrc(jsonData.downloadUrl);
          }
          setLoading(false);
        } else {
          // Local mode: response is the actual file blob
          objectUrl = URL.createObjectURL(response.data);
          setImageSrc(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading image:', err);
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      }
    };
    loadImage();
    return () => {
      mounted = false;
      // Only revoke if it's an object URL (starts with blob:)
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileId]);

  if (loading) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`}>
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 text-gray-400`}>
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  return <img src={imageSrc} alt={alt || fileName} className={className} />;
};

// Pagination settings
const ITEMS_PER_PAGE = 6; // Good for 2-column grid (3 rows)

const FileGallery = ({ projectId, taskId = null, fileType = null, onFileSelect = null, userRole = null, workflowStatus = null, refreshTrigger = 0 }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filterType, setFilterType] = useState(fileType || '');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'size'
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [measurementCompleted, setMeasurementCompleted] = useState(false);

  // 2D task state (for deletion logic)
  const [drawing2DTask, setDrawing2DTask] = useState(null);
  const [lastSubmissionDate, setLastSubmissionDate] = useState(null);

  // 3D task state (for deletion logic)
  const [drawing3DTask, setDrawing3DTask] = useState(null);
  const [last3DSubmissionDate, setLast3DSubmissionDate] = useState(null);

  // Determine allowed file types based on user role
  const isSiteDraughter = userRole === 'draughter_site';
  const is2DDraughter = userRole === 'draughter_2d';
  const is3DDesigner = userRole === 'designer';
  const hasFullAccess = hasDirectorAccess(userRole) || ['sid', 'asid', 'leader', 'manager'].includes(userRole);

  // Define file type restrictions per role
  // For viewing files
  const allowedViewFileTypes = isSiteDraughter
    ? [FILE_TYPE.SITE_PHOTOS, FILE_TYPE.SITE_MEASUREMENTS]
    : (is2DDraughter || is3DDesigner)
      ? [FILE_TYPE.SITE_PHOTOS, FILE_TYPE.SITE_MEASUREMENTS, FILE_TYPE.TWO_D_DRAWING, FILE_TYPE.THREE_D_RENDER, FILE_TYPE.DD_SPECS]
      : null; // null means all types allowed (full access roles)

  // For uploading files (more restrictive)
  const allowedUploadFileTypes = isSiteDraughter
    ? [FILE_TYPE.SITE_PHOTOS, FILE_TYPE.SITE_MEASUREMENTS]
    : is2DDraughter
      ? [FILE_TYPE.TWO_D_DRAWING]
      : is3DDesigner
        ? [FILE_TYPE.THREE_D_RENDER]
        : null; // Full access roles can upload all

  // Legacy support - use view types for backward compatibility
  const allowedFileTypes = allowedViewFileTypes;

  useEffect(() => {
    fetchFiles();
    // For site draughters, check if measurement is completed (for deletion logic)
    if (isSiteDraughter) {
      checkMeasurementStatus();
    }
    // For 2D draughters, check task status (for deletion logic)
    if (is2DDraughter) {
      check2DTaskStatus();
    }
    // For 3D designers, check task status (for deletion logic)
    if (is3DDesigner) {
      check3DTaskStatus();
    }
  }, [projectId, taskId, refreshTrigger]);

  // Check measurement status for site draughters (for deletion logic)
  const checkMeasurementStatus = async () => {
    try {
      const response = await api.get('/measurements/my');
      const measurements = response.data.data || response.data || [];
      const projectMeasurement = measurements.find(m => m.designProjectId === projectId);
      if (projectMeasurement && projectMeasurement.status === 'completed') {
        setMeasurementCompleted(true);
      }
    } catch (err) {
      console.error('Error checking measurement status:', err);
    }
  };

  // Check 2D task status for 2D draughters (for deletion logic)
  const check2DTaskStatus = async () => {
    try {
      const response = await drawing2DAPI.getMyTasks();
      const tasks = response.data.data || response.data || [];
      const projectTask = tasks.find(t => t.designProjectId === projectId);
      if (projectTask) {
        setDrawing2DTask(projectTask);
        if (projectTask.taskStatus === 'amendment_required') {
          const submissionResponse = await drawing2DAPI.getLastSubmissionDate(projectId);
          setLastSubmissionDate(submissionResponse.data?.data?.lastSubmissionDate || null);
        }
      }
    } catch (err) {
      console.error('Error checking 2D task status:', err);
    }
  };

  // Check 3D task status for 3D designers (for deletion logic)
  const check3DTaskStatus = async () => {
    try {
      const response = await drawing3DAPI.getMyTasks();
      const tasks = response.data.data || response.data || [];
      const projectTask = tasks.find(t => t.designProjectId === projectId);
      if (projectTask) {
        setDrawing3DTask(projectTask);
        if (projectTask.taskStatus === 'amendment_required') {
          const submissionResponse = await drawing3DAPI.getLastSubmissionDate(projectId);
          setLast3DSubmissionDate(submissionResponse.data?.data?.lastSubmissionDate || null);
        }
      }
    } catch (err) {
      console.error('Error checking 3D task status:', err);
    }
  };

  // Determine if user can delete a specific file
  const canDeleteFile = (file) => {
    // Full access roles can delete any file
    if (hasFullAccess) return true;

    // 2D draughters have special deletion rules
    if (is2DDraughter) {
      // Can only delete 2d_drawing files (not site_photos or site_measurements)
      if (file.fileType !== FILE_TYPE.TWO_D_DRAWING) return false;

      const taskStatus = drawing2DTask?.taskStatus;

      // Before first submission: can delete
      if (['assigned', 'in_progress'].includes(taskStatus)) {
        return true;
      }

      // After amendment: can only delete files uploaded AFTER the last submission
      if (taskStatus === 'amendment_required' && lastSubmissionDate) {
        const fileDate = new Date(file.createdAt);
        const submissionDate = new Date(lastSubmissionDate);
        return fileDate > submissionDate;
      }

      // After final submission or approval: cannot delete
      return false;
    }

    // 3D designers have special deletion rules (similar to 2D)
    if (is3DDesigner) {
      // Can only delete 3d_render files
      if (file.fileType !== FILE_TYPE.THREE_D_RENDER) return false;

      const taskStatus = drawing3DTask?.taskStatus;

      // Before first submission: can delete
      if (['assigned', 'in_progress'].includes(taskStatus)) {
        return true;
      }

      // After amendment: can only delete files uploaded AFTER the last submission
      if (taskStatus === 'amendment_required' && last3DSubmissionDate) {
        const fileDate = new Date(file.createdAt);
        const submissionDate = new Date(last3DSubmissionDate);
        return fileDate > submissionDate;
      }

      // After final submission or approval: cannot delete
      return false;
    }

    // Site draughters can delete their files only if measurement is not completed
    if (isSiteDraughter) {
      return !measurementCompleted;
    }

    // Others can delete
    return true;
  };

  const fetchFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/project-files/project/${projectId}`);
      // Backend returns { success: true, data: [...], count: N }
      let fetchedFiles = response.data.data || response.data || [];

      // Filter by task if specified
      if (taskId) {
        fetchedFiles = fetchedFiles.filter(f => f.designTaskId === taskId);
      }

      setFiles(fetchedFiles);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort files
  const getFilteredAndSortedFiles = () => {
    let result = [...files];

    // For site draughters, only show allowed file types
    if (allowedFileTypes) {
      result = result.filter(f => allowedFileTypes.includes(f.fileType));
    }

    // Apply filter
    if (filterType) {
      result = result.filter(f => f.fileType === filterType);
    }

    // Apply sort
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.fileName.localeCompare(b.fileName));
        break;
      case 'size':
        result.sort((a, b) => (b.fileSize || 0) - (a.fileSize || 0));
        break;
      case 'date':
      default:
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
    }

    return result;
  };

  const allFilteredFiles = getFilteredAndSortedFiles();

  // Pagination calculations
  const totalFiles = allFilteredFiles.length;
  const totalPages = Math.ceil(totalFiles / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const displayFiles = allFilteredFiles.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, sortBy]);

  // Get file icon based on extension
  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const iconMap = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      mp4: '🎥',
      mov: '🎥',
      dwg: '📐',
      dxf: '📐'
    };
    return iconMap[ext] || '📎';
  };

  // Check if file is an image
  const isImage = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
  };

  // Handle file download
  // Supports both local mode (direct blob) and R2 mode (presigned URL)
  const handleDownload = async (file) => {
    try {
      // Always fetch as blob first - works for both modes
      const response = await api.get(`/project-files/${file.id}/download`, {
        responseType: 'blob'
      });

      // Check if response is JSON (R2 mode returns JSON with downloadUrl)
      const contentType = response.headers['content-type'] || '';

      if (contentType.includes('application/json')) {
        // R2 mode: parse JSON to get downloadUrl
        const text = await response.data.text();
        const jsonData = JSON.parse(text);

        if (jsonData.downloadUrl) {
          // Use presigned URL directly (avoids CORS issues)
          const link = document.createElement('a');
          link.href = jsonData.downloadUrl;
          link.setAttribute('download', file.fileName);
          link.setAttribute('target', '_blank');
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      } else {
        // Local mode: response is the actual file blob
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file');
    }
  };

  // Handle file delete
  const handleDelete = async (file) => {
    if (!window.confirm(`Are you sure you want to delete "${file.fileName}"?`)) {
      return;
    }

    try {
      await api.delete(`/project-files/${file.id}`);
      setFiles(prev => prev.filter(f => f.id !== file.id));
    } catch (err) {
      console.error('Error deleting file:', err);
      alert('Failed to delete file');
    }
  };

  // Handle file preview
  // Supports both local mode (direct blob) and R2 mode (presigned URL)
  const handlePreview = async (file) => {
    setSelectedFile(file);
    setShowPreview(true);

    // Load image for preview if it's an image
    if (isImage(file.fileName)) {
      try {
        // Always fetch as blob - works for both modes
        const response = await api.get(`/project-files/${file.id}/download`, {
          responseType: 'blob'
        });

        // Check if response is JSON (R2 mode returns JSON with downloadUrl)
        const contentType = response.headers['content-type'] || '';

        if (contentType.includes('application/json')) {
          // R2 mode: parse JSON to get downloadUrl
          const text = await response.data.text();
          const jsonData = JSON.parse(text);
          if (jsonData.downloadUrl) {
            setPreviewImageSrc(jsonData.downloadUrl);
          }
        } else {
          // Local mode: response is the actual file blob
          const url = URL.createObjectURL(response.data);
          setPreviewImageSrc(url);
        }
      } catch (err) {
        console.error('Error loading preview:', err);
        setPreviewImageSrc(null);
      }
    }
  };

  // Close preview and cleanup
  const closePreview = () => {
    setShowPreview(false);
    setSelectedFile(null);
    if (previewImageSrc) {
      // Only revoke if it's a blob URL (not a presigned URL from R2)
      if (previewImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(previewImageSrc);
      }
      setPreviewImageSrc(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading files...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 font-medium">Error loading files</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchFiles}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with controls - Responsive layout */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Files ({totalFiles})
          </h3>
          <div className="flex items-center gap-2">
            {/* Upload button - touch friendly */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 active:scale-95 transition-all flex items-center gap-1.5 min-h-[44px] touch-manipulation"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden xs:inline">Upload</span>
            </button>
            {/* View mode toggle - touch friendly */}
            <div className="flex bg-gray-100 rounded-lg p-0.5 sm:p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 sm:p-2 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center touch-manipulation ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
              title="Grid view"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 sm:p-2 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center touch-manipulation ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
              title="List view"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            </div>
          </div>
        </div>

        {/* Filter controls - On separate row for narrow containers */}
        <div className="flex flex-wrap gap-2">
          {/* Sort dropdown - touch friendly */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex-1 min-w-0 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px] touch-manipulation"
          >
            <option value="date">Sort: Date</option>
            <option value="name">Sort: Name</option>
            <option value="size">Sort: Size</option>
          </select>

          {/* Filter dropdown - touch friendly */}
          {!fileType && (
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px] touch-manipulation"
            >
              <option value="">All Types</option>
              {(allowedFileTypes || Object.values(FILE_TYPE)).map(type => (
                <option key={type} value={type}>{FILE_TYPE_LABELS[type] || type}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Empty state */}
      {displayFiles.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No files</h3>
          <p className="mt-1 text-sm text-gray-500">
            {filterType ? 'No files match the current filter' : 'Upload files to get started'}
          </p>
        </div>
      )}

      {/* Grid view - Optimized for narrow containers */}
      {viewMode === 'grid' && displayFiles.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {displayFiles.map(file => (
            <div
              key={file.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group"
              onClick={() => onFileSelect ? onFileSelect(file) : handlePreview(file)}
            >
              {/* File preview/icon - Compact square */}
              <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden relative">
                {isImage(file.fileName) ? (
                  <AuthenticatedImage
                    fileId={file.id}
                    fileName={file.fileName}
                    className="w-full h-full object-cover"
                    alt={file.fileName}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center p-2">
                    <span className="text-4xl">{getFileIcon(file.fileName)}</span>
                    <span className="text-xs text-gray-400 mt-1 uppercase">
                      {file.fileName.split('.').pop()}
                    </span>
                  </div>
                )}
                {/* File type badge */}
                <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/60 text-white text-[10px] rounded font-medium">
                  {file.fileType?.replace('_', ' ').split(' ').map(w => w[0]).join('').toUpperCase() || 'FILE'}
                </span>
                {/* Amendment cycle badge */}
                {getAmendmentCycleShortLabel(file.amendmentCycle) && (
                  <span className={`absolute top-1 left-1 px-1.5 py-0.5 text-[10px] rounded font-medium ${
                    file.amendmentCycle === 0
                      ? 'bg-green-500/80 text-white'
                      : 'bg-orange-500/80 text-white'
                  }`}>
                    {getAmendmentCycleShortLabel(file.amendmentCycle)}
                  </span>
                )}
              </div>

              {/* File info - Compact */}
              <div className="p-2">
                <h4 className="font-medium text-gray-900 text-xs truncate" title={file.displayLabel || file.fileName}>
                  {file.displayLabel || file.fileName}
                </h4>
                {file.displayLabel && (
                  <p className="text-[10px] text-gray-400 truncate" title={file.fileName}>
                    {file.fileName}
                  </p>
                )}
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-gray-500">{formatFileSize(file.fileSize)}</span>
                  <span className="text-[10px] text-gray-400">{formatDate(file.createdAt)}</span>
                </div>

                {/* Actions - Touch-friendly row */}
                <div className="flex gap-1.5 mt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(file);
                    }}
                    className="flex-1 px-2 py-2 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center gap-1 min-h-[40px] touch-manipulation"
                    title="Download"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span className="hidden sm:inline">Download</span>
                  </button>
                  {canDeleteFile(file) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(file);
                      }}
                      className="px-2 py-2 bg-red-100 text-red-600 rounded-md text-xs font-medium hover:bg-red-600 hover:text-white active:scale-95 transition-all min-h-[40px] min-w-[40px] flex items-center justify-center touch-manipulation"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List view */}
      {viewMode === 'list' && displayFiles.length > 0 && (
        <div className="space-y-2">
          {displayFiles.map(file => (
            <div
              key={file.id}
              className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
              onClick={() => handlePreview(file)}
            >
              {/* Thumbnail/Icon */}
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                {isImage(file.fileName) ? (
                  <AuthenticatedImage
                    fileId={file.id}
                    fileName={file.fileName}
                    className="w-full h-full object-cover"
                    alt={file.fileName}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-2xl">{getFileIcon(file.fileName)}</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 text-sm truncate" title={file.displayLabel || file.fileName}>
                  {file.displayLabel || file.fileName}
                </h4>
                {file.displayLabel && (
                  <p className="text-[10px] text-gray-400 truncate">{file.fileName}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-medium uppercase">
                    {file.fileType?.replace('_', ' ') || 'other'}
                  </span>
                  {getAmendmentCycleShortLabel(file.amendmentCycle) && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      file.amendmentCycle === 0
                        ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {getAmendmentCycleShortLabel(file.amendmentCycle)}
                    </span>
                  )}
                  <span>{formatFileSize(file.fileSize)}</span>
                  <span className="text-gray-300">•</span>
                  <span>{formatDate(file.createdAt)}</span>
                </div>
              </div>

              {/* Actions - Touch-friendly */}
              <div className="flex gap-1.5 flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(file);
                  }}
                  className="p-2.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white active:scale-95 transition-all min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                  title="Download"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
                {canDeleteFile(file) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(file);
                    }}
                    className="p-2.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-600 hover:text-white active:scale-95 transition-all min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info message when file deletion is restricted */}
      {((isSiteDraughter && measurementCompleted) ||
        (is2DDraughter && ['submitted', 'approved'].includes(drawing2DTask?.taskStatus)) ||
        (is3DDesigner && ['submitted', 'approved'].includes(drawing3DTask?.taskStatus))) && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-amber-800">File Deletion Restricted</p>
              <p className="text-xs text-amber-700 mt-0.5">Some files cannot be deleted at this stage. Contact a manager if you need to make changes.</p>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          {/* Page info */}
          <span className="text-xs text-gray-500">
            {startIndex + 1}-{Math.min(endIndex, totalFiles)} of {totalFiles}
          </span>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            {/* Previous button */}
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // Show first, last, current, and adjacent pages
                  if (page === 1 || page === totalPages) return true;
                  if (Math.abs(page - currentPage) <= 1) return true;
                  return false;
                })
                .map((page, idx, arr) => (
                  <React.Fragment key={page}>
                    {/* Ellipsis for gaps */}
                    {idx > 0 && arr[idx - 1] !== page - 1 && (
                      <span className="px-1 text-gray-400 text-xs">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[28px] h-7 px-2 rounded-lg text-xs font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
            </div>

            {/* Next button */}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50" onClick={closePreview}>
          <div className="max-w-5xl max-h-full w-full mx-4" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 bg-white/20 text-white text-xs rounded font-medium uppercase">
                  {selectedFile.fileType?.replace('_', ' ') || 'File'}
                </span>
                <h3 className="font-semibold text-white text-lg truncate max-w-md">{selectedFile.fileName}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(selectedFile)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                <button
                  onClick={closePreview}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg overflow-hidden">
              {isImage(selectedFile.fileName) ? (
                <div className="flex items-center justify-center bg-gray-900 min-h-[300px]">
                  {previewImageSrc ? (
                    <img
                      src={previewImageSrc}
                      alt={selectedFile.fileName}
                      className="max-w-full max-h-[75vh] object-contain"
                    />
                  ) : (
                    <div className="flex items-center justify-center py-20">
                      <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 bg-gray-50">
                  <span className="text-8xl mb-4">{getFileIcon(selectedFile.fileName)}</span>
                  <p className="text-gray-600 text-lg font-medium">{selectedFile.fileName}</p>
                  <p className="text-gray-400 mt-2">{formatFileSize(selectedFile.fileSize)}</p>
                  <p className="text-gray-400 text-sm mt-1">Click Download to view this file</p>
                </div>
              )}
            </div>

            {/* Footer info */}
            <div className="flex items-center justify-between mt-4 px-2 text-white/70 text-sm">
              <span>Size: {formatFileSize(selectedFile.fileSize)}</span>
              <span>Uploaded: {formatDate(selectedFile.createdAt, true)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <UploadFilesModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        projectId={projectId}
        workflowStatus={workflowStatus}
        allowedFileTypes={allowedUploadFileTypes}
        onSuccess={() => {
          setShowUploadModal(false);
          fetchFiles();
        }}
      />
    </div>
  );
};

export default FileGallery;
